import React, { useState } from "react";
import styled from "styled-components";
import { Container } from "./styles";
import {
  DateIRLToErache,
  GetEracheDateComponents,
  formatIrlDate,
  formatIrlDateTime,
} from "../utils/dateConverters";

const SAMPLE_DATE = "2025-03-14T12:00:00";
const SAMPLE_UUID = "00000000-0000-4000-8000-000000000001";

function eracheYearPlaceholder(erache) {
  if (erache.isBeforeDok) {
    return `${erache.year} avant Dok`;
  }
  return String(erache.year);
}

function expandFormat(format, values) {
  let result = format;
  Object.entries(values).forEach(([k, v]) => {
    result = result.split(`{${k}}`).join(v ?? "");
  });
  return result;
}

function buildPreview({ dateIso = SAMPLE_DATE, preset, format, nom }) {
  const d = new Date(dateIso);
  const erache = GetEracheDateComponents(d);
  if (!erache) return "—";
  const eracheStr = DateIRLToErache(d, false);
  const irl = formatIrlDate(d);
  const irlDt = formatIrlDateTime(d);
  const ey = eracheYearPlaceholder(erache);

  if (format) {
    return expandFormat(format, {
      nom: nom ?? "",
      name: nom ?? "",
      erache: eracheStr,
      erache_year: ey,
      irl,
      irl_datetime: irlDt,
    });
  }
  if (preset === "erache_year") {
    return expandFormat("l'an de grâce {erache_year}", { erache_year: ey });
  }
  if (preset === "erache_and_nom" || preset === "erache_and_name") {
    return `${eracheStr}: ${nom ?? ""}`;
  }
  if (preset === "erache_and_irl") {
    return `${eracheStr} (${irl})`;
  }
  if (preset === "irl") {
    return irl;
  }
  if (preset === "irl_datetime") {
    return irlDt;
  }
  return eracheStr;
}

const EXAMPLES = [
  {
    wikitext: '<chrono date="2025-03-14" />',
    preview: () => buildPreview({}),
  },
  {
    wikitext: '<chrono date="2025-03-14" preset="erache_year" />',
    preview: () => buildPreview({ preset: "erache_year" }),
  },
  {
    wikitext:
      '<chrono date="2025-03-14" preset="erache_and_nom" nom="Fondation de Castelvieux" />',
    preview: () =>
      buildPreview({
        preset: "erache_and_nom",
        nom: "Fondation de Castelvieux",
      }),
  },
  {
    wikitext: '<chrono date="2025-03-14" preset="erache_and_irl" />',
    preview: () => buildPreview({ preset: "erache_and_irl" }),
  },
  {
    wikitext:
      '<chrono date="2025-03-14" format="{nom} — {erache}" nom="Bataille du Pont" />',
    preview: () =>
      buildPreview({
        format: "{nom} — {erache}",
        nom: "Bataille du Pont",
      }),
  },
  {
    wikitext: `<chrono uuid="${SAMPLE_UUID}" />`,
    preview: () => "nom, date… complétés après sync",
    previewItalic: true,
  },
];

const WikifieldExtensionInfos = () => {
  return (
    <Container>
      <PageWrap>
        <h1>Extension Wikifield — balises &lt;chrono&gt;</h1>

        <Lead>
          <p>
            <strong>Wikifield</strong> est le wiki du jeu ; <strong>Chronofield</strong>{" "}
            recense les dates et événements du monde. L’extension relie les deux :
            dans une page wiki, la balise <code>&lt;chrono&gt;</code> affiche une
            date (souvent en calendrier érachien) et, à l’enregistrement, peut{" "}
            <strong>créer un nouvel événement</strong> sur Chronofield ou{" "}
            <strong>renvoyer vers un événement déjà connu</strong>.
          </p>
        </Lead>

        <BulletList>
          <li>
            Pour <strong>créer</strong> un événement depuis le wiki :{" "}
            <code>date=</code> et <code>nom=</code> sont requis. Sans{" "}
            <code>nom</code>, rien n’est enregistré côté Chronofield.
          </li>
          <li>
            Pour <strong>citer</strong> un événement existant, on peut partir de sa
            fiche Chronofield — détail en note
            <FootnoteRef href="#note-uuid">*</FootnoteRef>.
          </li>
        </BulletList>

        <ExamplesBlock>
          {EXAMPLES.map((ex) => (
            <ExampleRow
              key={ex.wikitext}
              wikitext={ex.wikitext}
              preview={ex.preview()}
              previewItalic={ex.previewItalic}
            />
          ))}
        </ExamplesBlock>

        <Footnote id="note-uuid">
          <strong>*</strong> L’identifiant (<code>uuid=</code>) est en général
          ajouté tout seul après une première synchro. Vous pouvez aussi ne mettre
          que <code>uuid=</code> (copié depuis l’URL d’un événement) : l’extension
          complète nom et date — utile surtout pour réutiliser un évènement
          déjà lié.
        </Footnote>
      </PageWrap>
    </Container>
  );
};

function ExampleRow({ wikitext, preview, previewItalic }) {
  const [buttonColor, setButtonColor] = useState("var(--accent)");
  const [buttonText, setButtonText] = useState("Copier");

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(wikitext)
      .then(() => {
        setButtonText("Copié!");
        setButtonColor("var(--accent-hover)");
        setTimeout(() => {
          setButtonColor("var(--accent)");
          setButtonText("Copier");
        }, 2000);
      })
      .catch((err) =>
        console.error("Erreur lors de la copie dans le presse-papiers :", err)
      );
  };

  return (
    <Row>
      <PreviewCell as={previewItalic ? "em" : "div"} title={preview}>
        {preview}
      </PreviewCell>
      <CodeCell>
        <code>{wikitext}</code>
      </CodeCell>
      <CopyButton onClick={copyToClipboard} buttonColor={buttonColor}>
        {buttonText}
      </CopyButton>
    </Row>
  );
}

const PageWrap = styled.div`
  max-width: 960px;
  width: 100%;
  font-size: 0.95rem;
  & h1 {
    margin: 0 0 18px 0;
  }
`;

const Lead = styled.div`
  text-align: left;
  margin: 0 0 22px 0;
  line-height: 1.55;
  & p {
    margin: 0;
  }
  & code {
    font-size: 0.9em;
    background: var(--surface-2);
    color: var(--text);
    padding: 0 4px;
    border-radius: 3px;
  }
`;

const BulletList = styled.ul`
  text-align: left;
  margin: 0 0 28px 0;
  padding-left: 1.2em;
  line-height: 1.55;
  & li {
    margin-bottom: 14px;
  }
  & li:last-child {
    margin-bottom: 0;
  }
  & code {
    font-size: 0.9em;
    background: var(--surface-2);
    color: var(--text);
    padding: 0 4px;
    border-radius: 3px;
  }
`;

const FootnoteRef = styled.a`
  color: var(--text-muted);
  font-weight: 600;
  text-decoration: none;
  margin-left: 2px;
  &:hover {
    text-decoration: underline;
  }
`;

const ExamplesBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 28px;
`;

const Footnote = styled.p`
  text-align: left;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--text-muted);
  margin: 0;
  padding-top: 16px;
  border-top: 1px solid var(--border);
  max-width: 52em;
  & code {
    font-size: 0.95em;
    background: var(--surface-2);
    color: var(--text);
    padding: 0 3px;
    border-radius: 3px;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(180px, 2fr) auto;
  gap: 12px 14px;
  align-items: center;
  padding: 14px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 16px 12px;
  }
`;

const PreviewCell = styled.div`
  font-size: 0.88rem;
  line-height: 1.25;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 768px) {
    font-weight: 600;
  }
`;

const CodeCell = styled.div`
  code {
    font-size: 0.78rem;
    word-break: break-all;
    line-height: 1.3;
    display: block;
  }
`;

const CopyButton = styled.button`
  margin-left: 0;
  padding: 5px 10px;
  background-color: ${(props) => props.buttonColor || "var(--accent)"};
  color: #13110e;
  border: 1px solid var(--accent);
  border-radius: var(--radius-md);
  min-width: 88px;
  cursor: pointer;
  justify-self: end;
  align-self: center;
  &:hover {
    background-color: var(--accent-hover);
    border-color: var(--accent-hover);
    box-shadow: 0 0 0 3px var(--accent-bg);
  }
  @media (max-width: 768px) {
    justify-self: start;
    min-width: 100px;
  }
`;

export default WikifieldExtensionInfos;
